import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  labelClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getChhaMissedVisitForm,
  updateChhaMissedVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CalendarIcon,
  DeleteIcon,
  HelpingIcon,
  PhoneIcon,
  PlusIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  CHHA_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ATTEMPT_METHOD_OPTIONS,
  CHHA_MISSED_ARRAY_KEYS,
  COORDINATION_DISCIPLINE_OPTIONS,
  INTERVENTION_AFFECTED_OPTIONS,
  MISSED_REASON_OPTIONS,
  emptyAttemptRow,
  emptyChhaMissedVisitPayload,
  normalizeAttemptRows,
  toggleInList,
  today,
} from "./chhaMissedVisitContent";
import ChhaNotesCommonHeader from "./ChhaNotesCommonHeader";
import {
  normalizeChhaCommonHeaderState,
  resolveChhaNotesCommonHeader,
  withNormalizedChhaCommonHeader,
} from "./chhaNotesCommonHeaderUtils";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "../nurseNotes/nurseVisitCommon";

const MISSED_VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const toBool = (value) => value === true || value === "true" || value === 1;

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      compact
      className="min-w-0 w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const NotifyCard = ({
  title,
  toggleLabel,
  checked,
  dateValue,
  timeValue,
  dateName,
  timeName,
  canEdit,
  printMode,
  onCheckedChange,
  onDateChange,
  onTimeChange,
}) => (
  <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5 space-y-2">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-700">
      {title}
    </div>
    <label className="inline-flex items-center gap-2 min-h-7 text-[12px] font-medium text-slate-700">
      <input
        type="checkbox"
        className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        checked={Boolean(checked)}
        disabled={!canEdit}
        onChange={(e) => onCheckedChange(e.target.checked)}
      />
      <span>{toggleLabel}</span>
    </label>
    <div className="grid grid-cols-2 gap-2">
      <div className="min-w-0">
        <span className={labelClass}>Date</span>
        <DateFieldInput
          name={dateName}
          value={dateValue}
          canEdit={canEdit && Boolean(checked)}
          printMode={printMode}
          onChange={onDateChange}
        />
      </div>
      <div className="min-w-0">
        <span className={labelClass}>Time</span>
        {printMode ? (
          <div className={`${fieldClass} flex items-center`}>
            {timeValue || "—"}
          </div>
        ) : (
          <input
            type="time"
            name={timeName}
            className={fieldClass}
            value={timeValue || ""}
            disabled={!canEdit || !checked}
            onChange={(e) => onTimeChange(e.target.value)}
          />
        )}
      </div>
    </div>
  </div>
);

NotifyCard.propTypes = {
  title: PropTypes.string.isRequired,
  toggleLabel: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  dateValue: PropTypes.string,
  timeValue: PropTypes.string,
  dateName: PropTypes.string.isRequired,
  timeName: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onCheckedChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
};

const emptyValues = () => ({
  ...emptyChhaMissedVisitPayload(),
  missedVisitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const ChhaMissedVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyChhaMissedVisitPayload();
      const loadedPayload =
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
          ? data.payload
          : {};

      const legacyReasons = Array.isArray(loadedPayload.missedReasons)
        ? loadedPayload.missedReasons
        : [];
      const missedReason =
        String(loadedPayload.missedReason || "").trim() ||
        String(legacyReasons[0] || "");

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickNurseVisitMeta(loadedPayload, data),
        missedVisitDate:
          data.missedVisitDate || loadedPayload.missedVisitDate || today(),
        commonHeader: normalizeChhaCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        missedReason,
        reasonDetails: String(
          loadedPayload.reasonDetails ||
            loadedPayload.reasonOtherSpecify ||
            "",
        ),
        attemptRows: normalizeAttemptRows(loadedPayload.attemptRows),
        interventionsOther: String(loadedPayload.interventionsOther || ""),
        functionalImpact: String(loadedPayload.functionalImpact || ""),
        caseManagerNotified: toBool(loadedPayload.caseManagerNotified),
        caseManagerNotifiedDate: loadedPayload.caseManagerNotifiedDate || "",
        caseManagerNotifiedTime: loadedPayload.caseManagerNotifiedTime || "",
        physicianNotified: toBool(loadedPayload.physicianNotified),
        physicianNotifiedDate: loadedPayload.physicianNotifiedDate || "",
        physicianNotifiedTime: loadedPayload.physicianNotifiedTime || "",
        familyNotified: toBool(loadedPayload.familyNotified),
        familyNotifiedDate: loadedPayload.familyNotifiedDate || "",
        familyNotifiedTime: loadedPayload.familyNotifiedTime || "",
        rescheduledDate: loadedPayload.rescheduledDate || "",
        nextScheduledFrequency: loadedPayload.nextScheduledFrequency || "",
        clinicalStrategy: loadedPayload.clinicalStrategy || "",
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      CHHA_MISSED_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveChhaNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName || draft?.formName || "CHHA Missed Visit",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            missedVisitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyChhaMissedVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getChhaMissedVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveChhaNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateAttemptRow = (id, patch) => {
    setValues((prev) => ({
      ...prev,
      attemptRows: (prev.attemptRows || []).map((row) =>
        row.id === id ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addAttemptRow = () => {
    setValues((prev) => ({
      ...prev,
      attemptRows: [...(prev.attemptRows || []), emptyAttemptRow()],
    }));
  };

  const removeAttemptRow = (id) => {
    setValues((prev) => {
      const rows = prev.attemptRows || [];
      if (rows.length <= 1) return prev;
      return { ...prev, attemptRows: rows.filter((row) => row.id !== id) };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        CHHA_FORM_EDITOR_ROLES,
        "View only — only HHA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType: form?.formType || draft?.formType || clinicalForm?.formType,
      role,
      canEdit,
      preferredStaffId: draft?.visit?.staffId ?? null,
      currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
    });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      missedVisitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...clinicalFields
    } = values;

    const nestedPayload = withNormalizedChhaCommonHeader({
      ...clinicalFields,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : "",
      missedReason: String(values.missedReason || "").trim(),
      reasonDetails: String(values.reasonDetails || "").trim(),
      attemptRows: normalizeAttemptRows(values.attemptRows),
      interventionsOther: String(values.interventionsOther || "").trim(),
      functionalImpact: String(values.functionalImpact || "").trim(),
      caseManagerNotified: toBool(values.caseManagerNotified),
      caseManagerNotifiedDate: values.caseManagerNotifiedDate
        ? formatDateForInput(values.caseManagerNotifiedDate)
        : "",
      caseManagerNotifiedTime: values.caseManagerNotifiedTime || "",
      physicianNotified: toBool(values.physicianNotified),
      physicianNotifiedDate: values.physicianNotifiedDate
        ? formatDateForInput(values.physicianNotifiedDate)
        : "",
      physicianNotifiedTime: values.physicianNotifiedTime || "",
      familyNotified: toBool(values.familyNotified),
      familyNotifiedDate: values.familyNotifiedDate
        ? formatDateForInput(values.familyNotifiedDate)
        : "",
      familyNotifiedTime: values.familyNotifiedTime || "",
      rescheduledDate: values.rescheduledDate
        ? formatDateForInput(values.rescheduledDate)
        : "",
      nextScheduledFrequency: String(
        values.nextScheduledFrequency || "",
      ).trim(),
      clinicalStrategy: String(values.clinicalStrategy || "").trim(),
    });

    CHHA_MISSED_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error(
        "Add review comments before marking the form to be corrected",
      );
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateChhaMissedVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.missedVisitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const attemptRows = values.attemptRows || [];

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            CHHA Missed Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health aide documentation for missed personal care visits.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Missed Visit Date"
          dateValue={displayDateUS(values.missedVisitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Missed Visit Date
                </div>
                <InputDatePicker
                  name="missedVisitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.missedVisitDate) || ""}
                  onChange={(event) =>
                    setValue("missedVisitDate", event.target.value)
                  }
                  max={MISSED_VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <ChhaNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={AlertCircleIcon} title="7. Reason for Missed Visit">
          <ExclusiveChoiceGrid
            name="missedReason"
            options={MISSED_REASON_OPTIONS}
            value={values.missedReason || ""}
            canEdit={canEdit}
            onChange={(opt) => setValue("missedReason", opt)}
            columns={5}
          />
          <div className="mt-3">
            <Field label="Detailed Explanation">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.reasonDetails || ""}
                disabled={!canEdit}
                placeholder="Provide specific clinical details regarding the missed visit…"
                onChange={(e) => setValue("reasonDetails", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={PhoneIcon} title="8. Attempts Made to Complete Visit">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[12px]">
              <thead className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 w-16">Attempt #</th>
                  <th className="px-2 py-1.5 w-28">Time</th>
                  <th className="px-2 py-1.5 w-40">Method</th>
                  <th className="px-2 py-1.5">Outcome / Result</th>
                  {!printMode && canEdit ? (
                    <th className="px-2 py-1.5 w-10" />
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {attemptRows.map((row, index) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-semibold text-slate-600">
                      {String(index + 1).padStart(2, "0")}
                    </td>
                    <td className="px-2 py-1.5">
                      {printMode ? (
                        <span>{row.time || "—"}</span>
                      ) : (
                        <input
                          type="time"
                          className={fieldClass}
                          value={row.time || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateAttemptRow(row.id, { time: e.target.value })
                          }
                        />
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {printMode ? (
                        <span>{row.method || "—"}</span>
                      ) : (
                        <select
                          className={fieldClass}
                          value={row.method || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateAttemptRow(row.id, { method: e.target.value })
                          }
                        >
                          <option value="">Select…</option>
                          {ATTEMPT_METHOD_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {printMode ? (
                        <span>{row.outcome || "—"}</span>
                      ) : (
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.outcome || ""}
                          disabled={!canEdit}
                          placeholder={
                            index === 0
                              ? "No answer…"
                              : index === 1
                                ? "Voicemail left…"
                                : "Outcome…"
                          }
                          onChange={(e) =>
                            updateAttemptRow(row.id, {
                              outcome: e.target.value,
                            })
                          }
                        />
                      )}
                    </td>
                    {!printMode && canEdit ? (
                      <td className="px-2 py-1.5">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                          disabled={attemptRows.length <= 1}
                          onClick={() => removeAttemptRow(row.id)}
                          aria-label="Remove attempt"
                        >
                          <DeleteIcon size={12} />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!printMode && canEdit ? (
            <button
              type="button"
              onClick={addAttemptRow}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
            >
              <PlusIcon size={12} />
              Add attempt
            </button>
          ) : null}
        </Section>

        <Section icon={TargetIcon} title="9. Plan of Care Analysis">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-2">
              <SubHeading>CHHA Interventions / Goals Affected</SubHeading>
              <CheckboxGrid
                options={INTERVENTION_AFFECTED_OPTIONS}
                values={values.interventionsAffected}
                canEdit={canEdit}
                onToggle={(opt) =>
                  toggleListValue("interventionsAffected", opt)
                }
                columns={1}
              />
              <Field label="Other specific goals affected…">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.interventionsOther || ""}
                  disabled={!canEdit}
                  placeholder="Other specific goals affected…"
                  onChange={(e) =>
                    setValue("interventionsOther", e.target.value)
                  }
                />
              </Field>
            </div>
            <Field label="Functional Impact on CHHA Plan of Care">
              <textarea
                rows={8}
                className={textareaClass}
                value={values.functionalImpact || ""}
                disabled={!canEdit}
                placeholder="Describe clinical consequences of the missed visit (e.g., skin integrity risk, fall risk increased, nutritional gap)…"
                onChange={(e) => setValue("functionalImpact", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={HelpingIcon} title="10. Coordination & Notifications">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            <NotifyCard
              title="RN Case Manager"
              toggleLabel="Notified?"
              checked={values.caseManagerNotified}
              dateValue={values.caseManagerNotifiedDate}
              timeValue={values.caseManagerNotifiedTime}
              dateName="caseManagerNotifiedDate"
              timeName="caseManagerNotifiedTime"
              canEdit={canEdit}
              printMode={printMode}
              onCheckedChange={(v) => setValue("caseManagerNotified", v)}
              onDateChange={(v) => setValue("caseManagerNotifiedDate", v)}
              onTimeChange={(v) => setValue("caseManagerNotifiedTime", v)}
            />
            <NotifyCard
              title="Primary Physician"
              toggleLabel="Faxed/Called?"
              checked={values.physicianNotified}
              dateValue={values.physicianNotifiedDate}
              timeValue={values.physicianNotifiedTime}
              dateName="physicianNotifiedDate"
              timeName="physicianNotifiedTime"
              canEdit={canEdit}
              printMode={printMode}
              onCheckedChange={(v) => setValue("physicianNotified", v)}
              onDateChange={(v) => setValue("physicianNotifiedDate", v)}
              onTimeChange={(v) => setValue("physicianNotifiedTime", v)}
            />
            <NotifyCard
              title="Family / Caregiver"
              toggleLabel="Spoken to?"
              checked={values.familyNotified}
              dateValue={values.familyNotifiedDate}
              timeValue={values.familyNotifiedTime}
              dateName="familyNotifiedDate"
              timeName="familyNotifiedTime"
              canEdit={canEdit}
              printMode={printMode}
              onCheckedChange={(v) => setValue("familyNotified", v)}
              onDateChange={(v) => setValue("familyNotifiedDate", v)}
              onTimeChange={(v) => setValue("familyNotifiedTime", v)}
            />
          </div>
        </Section>

        <Section icon={CalendarIcon} title="11. Follow-Up Plan">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Field label="Rescheduled Date">
              <DateFieldInput
                name="rescheduledDate"
                value={values.rescheduledDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("rescheduledDate", v)}
              />
            </Field>
            <Field label="Next Scheduled Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.nextScheduledFrequency || ""}
                disabled={!canEdit}
                placeholder="e.g., 3w9"
                onChange={(e) =>
                  setValue("nextScheduledFrequency", e.target.value)
                }
              />
            </Field>
            <div>
              <SubHeading>Coordination with Discipline</SubHeading>
              <CheckboxGrid
                options={COORDINATION_DISCIPLINE_OPTIONS}
                values={values.coordinationDisciplines}
                canEdit={canEdit}
                onToggle={(opt) =>
                  toggleListValue("coordinationDisciplines", opt)
                }
                columns={2}
              />
            </div>
          </div>
          <div className="mt-3">
            <Field label="Clinical Strategy for Mitigation">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.clinicalStrategy || ""}
                disabled={!canEdit}
                placeholder="Describe how goals will be met despite this missed visit…"
                onChange={(e) => setValue("clinicalStrategy", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            canEdit={canEdit}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {!isReviewer && !printMode && form.reviewComments ? (
          <OfficeCommentsNote comments={form.reviewComments} />
        ) : null}
      </div>

      {!printMode && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

ChhaMissedVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default ChhaMissedVisitForm;
